import React, { useState } from "react";
import PageLayout from "../components/PageLayout";
import KPICard from "../components/KPICard";
import RAGBadge from "../components/RAGBadge";
import { useTheme } from "../DataContext";
import { DEMO_PROJECTS, generateMonthlyBurnData } from "../demoData";
import {
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Legend,
  ScatterChart, Scatter, ZAxis,
} from "recharts";

const fmt = (n) => n >= 1000000 ? `$${(n/1000000).toFixed(1)}M` : `$${(n/1000).toFixed(0)}K`;

export default function ProjectHealth() {
  const { theme } = useTheme();
  const [selected, setSelected] = useState(DEMO_PROJECTS[0]);

  const burnData = generateMonthlyBurnData(selected);

  // EVM Calculations
  const BAC  = selected.budget;
  const PV   = BAC * (selected.pct / 100) / selected.spi;
  const EV   = BAC * (selected.pct / 100);
  const AC   = selected.spent;
  const CPI  = selected.cpi;
  const SPI  = selected.spi;
  const CV   = EV - AC;
  const SV   = EV - PV;
  const EAC  = AC + (BAC - EV) / CPI;
  const ETC  = EAC - AC;
  const VAC  = BAC - EAC;
  const TCPI = (BAC - EV) / (BAC - AC);
  const CSI  = CPI * SPI;

  const radarData = [
    { subject: "Schedule", A: SPI * 100, fullMark: 120 },
    { subject: "Cost",     A: CPI * 100, fullMark: 120 },
    { subject: "Scope",    A: selected.pct,      fullMark: 120 },
    { subject: "Risk",     A: 100 - selected.failProb, fullMark: 120 },
    { subject: "Data",     A: 82, fullMark: 120 },
    { subject: "Benefits", A: 70, fullMark: 120 },
  ];

  // Scatter: all projects fail prob vs CPI
  const scatterData = DEMO_PROJECTS.map(p => ({
    x: p.cpi, y: p.failProb, z: p.budget / 100000, name: p.name, rag: p.rag,
  }));

  return (
    <PageLayout
      title="Project Health & Failure Prediction"
      subtitle="ML-driven failure probability, EVM metrics, and predictive EAC — P-02"
    >
      {/* Project Selector */}
      <div className="pmo-card" style={{ marginBottom: "20px" }}>
        <div className="section-title">Select Project</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
          {DEMO_PROJECTS.map(p => (
            <button
              key={p.id}
              onClick={() => setSelected(p)}
              style={{
                padding: "6px 12px", borderRadius: "8px",
                border: `1px solid ${selected.id === p.id ? theme.accent : theme.cardBorder}`,
                background: selected.id === p.id ? `${theme.accent}18` : "transparent",
                color: selected.id === p.id ? theme.accent : theme.textSecondary,
                cursor: "pointer", fontSize: "11px", fontWeight: 500,
                display: "flex", alignItems: "center", gap: "6px",
              }}
            >
              <span>{p.id}</span>
              <span style={{ maxWidth: "120px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</span>
              <RAGBadge status={p.rag} />
            </button>
          ))}
        </div>
      </div>

      {/* Failure Prediction Banner */}
      <div style={{
        padding: "16px 20px", borderRadius: "12px", marginBottom: "20px",
        background: selected.failProb > 60
          ? `linear-gradient(135deg, ${theme.danger}18, ${theme.danger}08)`
          : selected.failProb > 30
          ? `linear-gradient(135deg, ${theme.warning}18, ${theme.warning}08)`
          : `linear-gradient(135deg, ${theme.success}18, ${theme.success}08)`,
        border: `1px solid ${selected.failProb > 60 ? theme.danger : selected.failProb > 30 ? theme.warning : theme.success}44`,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        flexWrap: "wrap", gap: "12px",
      }}>
        <div>
          <div style={{ fontFamily: "'Orbitron',monospace", fontSize: "11px", letterSpacing: "1px", color: theme.textMuted, marginBottom: "4px" }}>
            ML FAILURE PROBABILITY — ENSEMBLE MODEL (RF + XGB + LightGBM)
          </div>
          <div style={{ fontFamily: "'Orbitron',monospace", fontSize: "32px", fontWeight: 900,
            color: selected.failProb > 60 ? theme.danger : selected.failProb > 30 ? theme.warning : theme.success,
            filter: `drop-shadow(0 0 8px ${selected.failProb > 60 ? theme.danger : selected.failProb > 30 ? theme.warning : theme.success}66)`,
          }}>
            {selected.failProb}%
          </div>
          <div style={{ fontSize: "12px", color: theme.textMuted, marginTop: "4px" }}>
            Confidence interval: ±3.2% · Last updated: 2026-05-11
          </div>
        </div>
        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: "10px", color: theme.textMuted, letterSpacing: "0.5px" }}>SELECTED</div>
            <div style={{ fontSize: "13px", color: theme.textPrimary, fontWeight: 600, maxWidth: "200px" }}>{selected.name}</div>
          </div>
          <RAGBadge status={selected.rag} size="lg" />
        </div>
      </div>

      {/* EVM KPI Row */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "16px", marginBottom: "20px" }}>
        <KPICard label="CPI" value={CPI.toFixed(3)} color={CPI >= 1 ? theme.success : CPI >= 0.9 ? theme.warning : theme.danger}
          sub={`CV = ${fmt(Math.abs(CV))} ${CV >= 0 ? "under" : "over"}`} />
        <KPICard label="SPI" value={SPI.toFixed(3)} color={SPI >= 1 ? theme.success : SPI >= 0.9 ? theme.warning : theme.danger}
          sub={`SV = ${fmt(Math.abs(SV))} ${SV >= 0 ? "ahead" : "behind"}`} />
        <KPICard label="EAC (Forecast)" value={fmt(EAC)} color={EAC > BAC ? theme.danger : theme.success}
          sub={`BAC = ${fmt(BAC)} · VAC = ${fmt(VAC)}`} />
        <KPICard label="TCPI" value={TCPI.toFixed(3)} color={TCPI > 1.1 ? theme.danger : TCPI > 1.0 ? theme.warning : theme.success}
          sub="To-Complete Performance" />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "16px", marginBottom: "24px" }}>
        <KPICard label="BAC"   value={fmt(BAC)}  color={theme.textSecondary} sub="Budget at Completion" />
        <KPICard label="EV"    value={fmt(EV)}   color={theme.accent}        sub="Earned Value" />
        <KPICard label="AC"    value={fmt(AC)}   color={theme.cyan}          sub="Actual Cost" />
        <KPICard label="CSI"   value={CSI.toFixed(3)} color={CSI >= 0.9 ? theme.success : theme.danger}
          sub="Cost-Schedule Index" />
      </div>

      {/* Charts */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: "24px" }}>
        {/* Burn Chart */}
        <div className="pmo-card">
          <div className="section-title">Budget Burn — Planned vs Actual</div>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={burnData} margin={{ top: 5, right: 10, bottom: 0, left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={`${theme.cardBorder}66`} />
              <XAxis dataKey="month" tick={{ fill: theme.textMuted, fontSize: 10 }} />
              <YAxis tickFormatter={v => `$${(v/1000).toFixed(0)}K`} tick={{ fill: theme.textMuted, fontSize: 10 }} />
              <Tooltip formatter={v => fmt(v)} contentStyle={{ background: theme.card, border: `1px solid ${theme.cardBorder}`, borderRadius: "8px" }} />
              <Legend wrapperStyle={{ fontSize: "11px" }} />
              <Line type="monotone" dataKey="planned"  stroke={theme.cyan}    strokeWidth={2} dot={false} name="Planned" />
              <Line type="monotone" dataKey="actual"   stroke={theme.accent}  strokeWidth={2} dot={false} name="Actual" />
              <Line type="monotone" dataKey="forecast" stroke={theme.warning} strokeWidth={1} strokeDasharray="4 4" dot={false} name="Forecast" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Health Radar */}
        <div className="pmo-card">
          <div className="section-title">Project Health Radar</div>
          <ResponsiveContainer width="100%" height={200}>
            <RadarChart data={radarData}>
              <PolarGrid stroke={`${theme.cardBorder}88`} />
              <PolarAngleAxis dataKey="subject" tick={{ fill: theme.textMuted, fontSize: 10 }} />
              <PolarRadiusAxis angle={90} domain={[0, 120]} tick={false} />
              <Radar name={selected.id} dataKey="A" stroke={theme.accent} fill={theme.accent} fillOpacity={0.2} strokeWidth={2} />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* EVM Table + Scatter */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: "24px" }}>
        <div className="pmo-card">
          <div className="section-title">EVM Formulae & Values</div>
          <table>
            <thead><tr><th>Metric</th><th>Formula</th><th>Value</th><th>Status</th></tr></thead>
            <tbody>
              {[
                { m:"BAC", f:"Budget at Completion",                       v:fmt(BAC),          s:null },
                { m:"PV",  f:"BAC × Planned %",                            v:fmt(PV),           s:null },
                { m:"EV",  f:"BAC × % Complete",                           v:fmt(EV),           s:null },
                { m:"AC",  f:"Actual Cost Incurred",                       v:fmt(AC),           s:null },
                { m:"CPI", f:"EV ÷ AC",                                    v:CPI.toFixed(3),    s:CPI >= 1 ? "green" : CPI >= 0.9 ? "amber" : "red" },
                { m:"SPI", f:"EV ÷ PV",                                    v:SPI.toFixed(3),    s:SPI >= 1 ? "green" : SPI >= 0.9 ? "amber" : "red" },
                { m:"CV",  f:"EV − AC",                                    v:fmt(Math.abs(CV)), s:CV >= 0 ? "green" : "red" },
                { m:"SV",  f:"EV − PV",                                    v:fmt(Math.abs(SV)), s:SV >= 0 ? "green" : "red" },
                { m:"EAC", f:"AC + (BAC−EV) ÷ CPI",                       v:fmt(EAC),          s:EAC <= BAC ? "green" : "red" },
                { m:"ETC", f:"EAC − AC",                                   v:fmt(ETC),          s:null },
                { m:"VAC", f:"BAC − EAC",                                  v:fmt(Math.abs(VAC)),s:VAC >= 0 ? "green" : "red" },
                { m:"TCPI",f:"(BAC−EV) ÷ (BAC−AC)",                       v:TCPI.toFixed(3),   s:TCPI <= 1.1 ? "green" : "red" },
                { m:"CSI", f:"CPI × SPI",                                  v:CSI.toFixed(3),    s:CSI >= 0.9 ? "green" : CSI >= 0.8 ? "amber" : "red" },
              ].map(row => (
                <tr key={row.m}>
                  <td style={{ fontFamily: "'Share Tech Mono',monospace", color: theme.accent, fontWeight: 700 }}>{row.m}</td>
                  <td style={{ fontSize: "11px" }}>{row.f}</td>
                  <td style={{ fontFamily: "'Share Tech Mono',monospace", fontWeight: 600 }}>{row.v}</td>
                  <td>{row.s ? <RAGBadge status={row.s} /> : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Portfolio CPI vs Fail Risk Scatter */}
        <div className="pmo-card">
          <div className="section-title">Portfolio: CPI vs Failure Risk</div>
          <div style={{ fontSize: "11px", color: theme.textMuted, marginBottom: "8px" }}>Bubble size = budget · X = CPI · Y = Failure Probability %</div>
          <ResponsiveContainer width="100%" height={280}>
            <ScatterChart margin={{ top: 10, right: 10, bottom: 0, left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={`${theme.cardBorder}66`} />
              <XAxis type="number" dataKey="x" name="CPI" domain={[0.7, 1.2]} tick={{ fill: theme.textMuted, fontSize: 10 }} label={{ value: "CPI", fill: theme.textMuted, fontSize: 11, position: "bottom" }} />
              <YAxis type="number" dataKey="y" name="Fail%" tick={{ fill: theme.textMuted, fontSize: 10 }} label={{ value: "Fail %", angle: -90, fill: theme.textMuted, fontSize: 11 }} />
              <ZAxis type="number" dataKey="z" range={[40, 200]} />
              <Tooltip cursor={{ strokeDasharray: "3 3" }} content={({ payload }) => {
                if (!payload?.length) return null;
                const d = payload[0].payload;
                return (
                  <div style={{ background: theme.card, border: `1px solid ${theme.cardBorder}`, padding: "8px 12px", borderRadius: "8px", fontSize: "11px" }}>
                    <div style={{ color: theme.accent, fontWeight: 600, marginBottom: "4px" }}>{d.name}</div>
                    <div>CPI: {d.x} · Fail Risk: {d.y}%</div>
                    <RAGBadge status={d.rag} />
                  </div>
                );
              }} />
              <Scatter data={scatterData.map(d => ({ ...d, fill: d.rag === "red" ? theme.danger : d.rag === "amber" ? theme.warning : theme.success }))}
                shape={(props) => {
                  const { cx, cy, r, payload } = props;
                  const color = payload.rag === "red" ? theme.danger : payload.rag === "amber" ? theme.warning : theme.success;
                  return <circle cx={cx} cy={cy} r={r / 2} fill={`${color}88`} stroke={color} strokeWidth={1.5} />;
                }}
              />
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* All Projects Summary */}
      <div className="pmo-card">
        <div className="section-title">All Projects — Health Summary</div>
        <table>
          <thead>
            <tr>{["ID","Project","CPI","SPI","% Done","EAC","VAC","Fail Risk","RAG"].map(h => <th key={h}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {DEMO_PROJECTS.map(p => {
              const pEV = p.budget * (p.pct / 100);
              const pEAC = p.spent + (p.budget - pEV) / p.cpi;
              const pVAC = p.budget - pEAC;
              return (
                <tr key={p.id} style={{ cursor: "pointer" }} onClick={() => setSelected(p)}>
                  <td style={{ fontFamily: "'Share Tech Mono',monospace", color: theme.accent }}>{p.id}</td>
                  <td style={{ color: theme.textPrimary, fontWeight: selected.id === p.id ? 600 : 400 }}>{p.name}</td>
                  <td style={{ color: p.cpi >= 1 ? theme.success : p.cpi >= 0.9 ? theme.warning : theme.danger, fontFamily: "'Share Tech Mono',monospace" }}>{p.cpi.toFixed(2)}</td>
                  <td style={{ color: p.spi >= 1 ? theme.success : p.spi >= 0.9 ? theme.warning : theme.danger, fontFamily: "'Share Tech Mono',monospace" }}>{p.spi.toFixed(2)}</td>
                  <td>{p.pct}%</td>
                  <td style={{ fontFamily: "'Share Tech Mono',monospace" }}>{fmt(pEAC)}</td>
                  <td style={{ color: pVAC >= 0 ? theme.success : theme.danger, fontFamily: "'Share Tech Mono',monospace" }}>{fmt(Math.abs(pVAC))}</td>
                  <td><span style={{ padding: "2px 8px", borderRadius: "10px", fontSize: "11px", fontWeight: 700, background: p.failProb > 60 ? `${theme.danger}20` : p.failProb > 30 ? `${theme.warning}20` : `${theme.success}20`, color: p.failProb > 60 ? theme.danger : p.failProb > 30 ? theme.warning : theme.success }}>{p.failProb}%</span></td>
                  <td><RAGBadge status={p.rag} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </PageLayout>
  );
}
